import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import PlatformContratForm from "@/pages/super-admin/components/PlatformContratForm";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import {
  PLATFORM_CONTRAT_STATUT_LABELS,
  PLATFORM_CONTRAT_STATUT_OPTIONS,
  PLATFORM_CONTRAT_STATUT_TONES,
  PLATFORM_SCOPE_LABELS,
  platformContratReference,
  platformLocataireNomComplet,
  type PlatformContrat,
  type PlatformScope,
} from "@/store/platform/types";
import { BIEN_TYPE_LABELS, type BienType } from "@/pages/proprietaire/biens/types";
import type { ExportColumn } from "@/utils/export";
import { formatDate, formatFcfa } from "@/utils/format";

type SortKey = "recent" | "loyer" | "debut";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "loyer", label: "Loyer décroissant" },
  { value: "debut", label: "Date de début" },
];

const statutOptions = [{ value: "", label: "Tous les statuts" }, ...PLATFORM_CONTRAT_STATUT_OPTIONS];

export default function SuperAdminContratsPage() {
  const { contrats, getImmeuble, getBien, getLocataire, removeContrat } = usePlatformData();

  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<PlatformContrat | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlatformContrat | null>(null);

  const scopeOptions = [
    { value: "", label: "Tous les espaces" },
    ...(Object.keys(PLATFORM_SCOPE_LABELS) as PlatformScope[]).map((scope) => ({
      value: scope,
      label: PLATFORM_SCOPE_LABELS[scope],
    })),
  ];

  const stats = useMemo(
    () => ({
      total: contrats.length,
      actifs: contrats.filter((item) => item.statut === "actif").length,
      brouillons: contrats.filter((item) => item.statut === "brouillon").length,
      expires: contrats.filter((item) => item.statut === "expire").length,
    }),
    [contrats]
  );

  const immeubleNom = (immeubleId: string) => getImmeuble(immeubleId)?.nom ?? "Immeuble inconnu";

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = contrats.filter((item) => {
      const matchesScope = !scopeFilter || item.scope === scopeFilter;
      const matchesStatut = !statutFilter || item.statut === statutFilter;
      const bien = getBien(item.bienId);
      const locataire = getLocataire(item.locataireId);
      const matchesSearch =
        !term ||
        [
          platformContratReference(item.id),
          immeubleNom(item.immeubleId),
          bien ? BIEN_TYPE_LABELS[bien.type as BienType] : "",
          bien?.reference ?? "",
          locataire ? platformLocataireNomComplet(locataire) : "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesScope && matchesStatut && matchesSearch;
    });
    return result.sort((a, b) => {
      if (sort === "loyer") return b.loyerMensuel - a.loyerMensuel;
      if (sort === "debut") return new Date(b.dateDebut).getTime() - new Date(a.dateDebut).getTime();
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrats, search, scopeFilter, statutFilter, sort]);

  const isFiltering = Boolean(search.trim() || scopeFilter || statutFilter);

  const exportColumns: ExportColumn<PlatformContrat>[] = [
    { header: "Référence", value: (item) => platformContratReference(item.id) },
    { header: "Espace", value: (item) => PLATFORM_SCOPE_LABELS[item.scope] },
    { header: "Immeuble", value: (item) => immeubleNom(item.immeubleId) },
    {
      header: "Bien",
      value: (item) => {
        const bien = getBien(item.bienId);
        return bien ? `${BIEN_TYPE_LABELS[bien.type as BienType]}${bien.reference ? ` ${bien.reference}` : ""}` : "";
      },
    },
    {
      header: "Locataire",
      value: (item) => {
        const locataire = getLocataire(item.locataireId);
        return locataire ? platformLocataireNomComplet(locataire) : "";
      },
    },
    { header: "Date de début", value: (item) => formatDate(item.dateDebut) },
    { header: "Date de fin", value: (item) => formatDate(item.dateFin) },
    { header: "Loyer mensuel (FCFA)", value: (item) => formatFcfa(item.loyerMensuel) },
    { header: "Statut", value: (item) => PLATFORM_CONTRAT_STATUT_LABELS[item.statut] },
  ];

  const resetFilters = () => {
    setSearch("");
    setScopeFilter("");
    setStatutFilter("");
  };

  const columns: DataTableColumn<PlatformContrat>[] = [
    {
      key: "contrat",
      header: "Contrat",
      className: "min-w-[180px]",
      render: (contrat) => {
        const bien = getBien(contrat.bienId);
        return (
          <div className="flex flex-col">
            <span className="font-label font-medium text-foreground-950">
              {platformContratReference(contrat.id)}
            </span>
            <span className="text-xs text-foreground-500">
              {bien ? BIEN_TYPE_LABELS[bien.type as BienType] : "Bien non renseigné"}
            </span>
          </div>
        );
      },
    },
    {
      key: "locataire",
      header: "Locataire",
      className: "min-w-[170px]",
      render: (contrat) => {
        const locataire = getLocataire(contrat.locataireId);
        return (
          <span className="text-foreground-700">
            {locataire ? platformLocataireNomComplet(locataire) : "—"}
          </span>
        );
      },
    },
    {
      key: "emplacement",
      header: "Bien · Immeuble",
      className: "min-w-[190px]",
      render: (contrat) => {
        const bien = getBien(contrat.bienId);
        const immeuble = getImmeuble(contrat.immeubleId);
        const emplacement = [bien ? BIEN_TYPE_LABELS[bien.type as BienType] : "", immeuble?.nom ?? ""]
          .filter(Boolean)
          .join(" · ");
        return <span className="text-foreground-700">{emplacement || "—"}</span>;
      },
    },
    {
      key: "scope",
      header: "Espace",
      render: (contrat) => (
        <Badge tone={contrat.scope === "agence" ? "accent" : "primary"}>
          {PLATFORM_SCOPE_LABELS[contrat.scope]}
        </Badge>
      ),
    },
    {
      key: "periode",
      header: "Période",
      className: "min-w-[200px]",
      render: (contrat) => (
        <span className="text-foreground-700">
          {formatDate(contrat.dateDebut)} → {contrat.dateFin ? formatDate(contrat.dateFin) : "en cours"}
        </span>
      ),
    },
    {
      key: "loyer",
      header: "Loyer / mois",
      align: "right",
      className: "min-w-[120px]",
      render: (contrat) => (
        <span className="font-label font-medium text-foreground-950">{formatFcfa(contrat.loyerMensuel)}</span>
      ),
    },
    {
      key: "statut",
      header: "Statut",
      render: (contrat) => (
        <Badge tone={PLATFORM_CONTRAT_STATUT_TONES[contrat.statut]}>
          {PLATFORM_CONTRAT_STATUT_LABELS[contrat.statut]}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[200px]",
      render: (contrat) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/super-admin/contrats/${contrat.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir le détail
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions onEdit={() => setEditing(contrat)} onDelete={() => setPendingDelete(contrat)} />
        </div>
      ),
    },
  ];

  const confirmDelete = () => {
    if (!pendingDelete) return;
    removeContrat(pendingDelete.id);
    setPendingDelete(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Contrats</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Vue globale des contrats de la plateforme. Les contrats sont la donnée partagée entre l'espace
            Propriétaire, l'espace Agence et le Super Admin : une correction ici est visible partout. La création se
            fait depuis l'espace concerné.
          </p>
        </div>
        <ExportMenu
          rows={filtered}
          columns={exportColumns}
          filename="super-admin-contrats"
          title="Contrats"
          subtitle="Contrats affichés selon les filtres"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-file-text-line" label="Contrats" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actifs" value={String(stats.actifs)} tone="secondary" />
        <StatCard icon="ri-draft-line" label="Brouillons" value={String(stats.brouillons)} tone="accent" />
        <StatCard icon="ri-history-line" label="Expirés" value={String(stats.expires)} tone="neutral" />
      </div>

      {contrats.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col gap-1.5 xl:col-span-2">
              <label htmlFor="sa-ctr-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="sa-ctr-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Référence, immeuble, bien, locataire..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="sa-ctr-scope" className="text-xs font-label font-medium text-foreground-700">
                Espace
              </label>
              <Select
                id="sa-ctr-scope"
                value={scopeFilter}
                onChange={(event) => setScopeFilter(event.target.value)}
                options={scopeOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="sa-ctr-statut" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="sa-ctr-statut"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={statutOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500">
              {filtered.length} contrat{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {contrats.length}
            </p>
            <div className="flex items-center gap-3">
              {isFiltering && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  Réinitialiser
                </button>
              )}
              <div className="w-48">
                <Select
                  id="sa-ctr-sort"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                  options={sortOptions}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {contrats.length === 0 && (
        <EmptyState
          icon="ri-file-text-line"
          title="Aucun contrat"
          description="Les contrats créés par les propriétaires et les agences apparaîtront ici automatiquement."
        />
      )}

      {contrats.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun contrat ne correspond à votre recherche ou aux filtres sélectionnés."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(contrat) => contrat.id}
          minWidthClass="min-w-[1300px]"
          caption="Contrats"
        />
      )}

      <PlatformContratForm open={Boolean(editing)} onClose={() => setEditing(null)} contrat={editing} />

      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        description={
          pendingDelete
            ? `Supprimer le contrat « ${platformContratReference(pendingDelete.id)} » ? Cette action est définitive et le retirera des espaces Propriétaire et Agence.`
            : ""
        }
      />
    </div>
  );
}